// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — projection + framing maths. The map is a flat equirectangular
// canvas that AUTO-FRAMES to the countries in play (start, dest, chain) so a
// regional route stays legible on a phone. Pure functions, unit-tested.

export type LonLat = readonly [number, number];

export interface Frame {
  /** Longitude/latitude bounds of the view. */
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

/** Minimum degrees a frame spans, so a one-hop route isn't zoomed to absurdity. */
export const MIN_SPAN = 26;

/**
 * A stable frame around a set of centroids: their bounding box, padded 30% and
 * grown to at least MIN_SPAN in each axis, clamped to the world. Adding a hop
 * inside the existing box does not move the frame — the padding absorbs it.
 */
export function frameFor(points: readonly LonLat[]): Frame {
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const [lon, lat] of points) {
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  if (!Number.isFinite(minLon)) {
    minLon = -180;
    maxLon = 180;
    minLat = -60;
    maxLat = 80;
  }
  let spanLon = maxLon - minLon;
  let spanLat = maxLat - minLat;
  const padLon = Math.max(spanLon * 0.3, 6);
  const padLat = Math.max(spanLat * 0.3, 6);
  minLon -= padLon;
  maxLon += padLon;
  minLat -= padLat;
  maxLat += padLat;
  spanLon = maxLon - minLon;
  spanLat = maxLat - minLat;
  if (spanLon < MIN_SPAN) {
    const cx = (minLon + maxLon) / 2;
    minLon = cx - MIN_SPAN / 2;
    maxLon = cx + MIN_SPAN / 2;
  }
  if (spanLat < MIN_SPAN) {
    const cy = (minLat + maxLat) / 2;
    minLat = cy - MIN_SPAN / 2;
    maxLat = cy + MIN_SPAN / 2;
  }
  // Clamp to the world, preserving span where possible.
  if (minLat < -85) {
    maxLat += -85 - minLat;
    minLat = -85;
  }
  if (maxLat > 85) {
    minLat -= maxLat - 85;
    maxLat = 85;
  }
  return { minLon, minLat, maxLon, maxLat };
}
